import { z } from "zod";

/**
 * US-E15.F2-0177-S02 (@UC9214) — netting VIEW response DTO.
 *
 * ⚠️ VIEW đọc-only (P1.5): DTO này là kết quả TÍNH TOÁN từ DebtEntry, KHÔNG phản ánh bảng lưu net.
 * Tiền = string minor-unit (BigInt-safe qua wire). `netMinor` CÓ DẤU (âm = nợ ròng, dương = được nợ).
 *
 * 🔴 US-E21.F4-0216 (@UC9346) — REDACTION theo vai trên bề mặt ADMIN: tiền `nullable`.
 *  - `netMinor` / `suggestions[].amountMinor` = `null` ⇒ NGƯỜI GỌI KHÔNG ĐƯỢC XEM TIỀN (SUPPORT/
 *    READ_ONLY). KHÔNG DÙNG `0` — `0` là **nói dối về tiền** (0 nghĩa là "hết nợ", null nghĩa là
 *    "bạn không được thấy"). App-lane (end-user, `/funds/:id/netting`) LUÔN trả string (không redact).
 *  - `displayName` vai đọc-thấp = định danh ĐÃ CHE (mã member), KHÔNG phải tên thật.
 */

/** Số nguyên minor CÓ DẤU (net per member có thể âm). */
const SIGNED_MONEY_MINOR_STRING = /^-?\d+$/;
/** Số nguyên minor KHÔNG âm (số tiền chuyển luôn > 0, nhưng cho phép 0 để an toàn parse). */
const UNSIGNED_MONEY_MINOR_STRING = /^\d+$/;

/** Net của 1 member: + được nợ, − nợ. `null` = redact theo vai (KHÔNG phải 0). */
export const nettingMemberSchema = z
  .object({
    memberId: z.string().uuid(),
    displayName: z.string(),
    netMinor: z.string().regex(SIGNED_MONEY_MINOR_STRING).nullable(),
  })
  .strict();

/** 1 gợi ý "ai trả ai": from trả to `amountMinor` (> 0). `null` = redact theo vai. */
export const nettingSuggestionSchema = z
  .object({
    fromMemberId: z.string().uuid(),
    toMemberId: z.string().uuid(),
    amountMinor: z.string().regex(UNSIGNED_MONEY_MINOR_STRING).nullable(),
  })
  .strict();

/** GET /v1/funds/:fundId/netting response. */
export const nettingViewResponseSchema = z
  .object({
    fundId: z.string().uuid(),
    currency: z.string().length(3),
    members: z.array(nettingMemberSchema),
    suggestions: z.array(nettingSuggestionSchema),
  })
  .strict();

export type NettingMember = z.infer<typeof nettingMemberSchema>;
export type NettingSuggestion = z.infer<typeof nettingSuggestionSchema>;
export type NettingViewResponse = z.infer<typeof nettingViewResponseSchema>;
